'use client';

import * as React from 'react';
import { faExclamation, faTimes, faInfo, faCheck } from '@fortawesome/pro-light-svg-icons';

import type { ToastProps } from '@/components/ui/toast.component';

const TOAST_LIMIT = 1;
const TOAST_REMOVE_DELAY = 1000000;

type ActionOnToast = {
  text: string;
  onClick: () => void;
};

type ToasterToast = ToastProps & {
  id: string;
  action?: ActionOnToast;
  closable?: boolean;
  duration?: number;
};

let count = 0;

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

enum ActionType {
  ADD_TOAST = 'ADD_TOAST',
  UPDATE_TOAST = 'UPDATE_TOAST',
  DISMISS_TOAST = 'DISMISS_TOAST',
  REMOVE_TOAST = 'REMOVE_TOAST',
};

type Action
  = | {
    type: ActionType.ADD_TOAST;
    toast: ToasterToast;
  }
  | {
    type: ActionType.UPDATE_TOAST;
    toast: Partial<ToasterToast>;
  }
  | {
    type: ActionType.DISMISS_TOAST;
    toastId?: ToasterToast['id'];
  }
  | {
    type: ActionType.REMOVE_TOAST;
    toastId?: ToasterToast['id'];
  };

interface State {
  toasts: ToasterToast[];
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: ActionType.REMOVE_TOAST,
      toastId: toastId,
    });
  }, TOAST_REMOVE_DELAY);

  toastTimeouts.set(toastId, timeout);
};

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ActionType.ADD_TOAST:
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case ActionType.UPDATE_TOAST:
      return {
        ...state,
        toasts: state.toasts.map((t) => (t.id === action.toast.id ? { ...t, ...action.toast } : t)),
      };

    case ActionType.DISMISS_TOAST: {
      const { toastId } = action;

      // ! Side effects ! - This could be extracted into a dismissToast() action,
      // but I'll keep it here for simplicity
      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id);
        });
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t,
        ),
      };
    }
    case ActionType.REMOVE_TOAST:
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };
  }
};

const listeners: Array<(state: State) => void> = [];

let memoryState: State = { toasts: [] };

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

type Toast = Omit<ToasterToast, 'id'>;

function toast(props: Toast) {
  const id = genId();

  const update = (props: ToasterToast) =>
    dispatch({
      type: ActionType.UPDATE_TOAST,
      toast: { ...props, id },
    });
  const dismiss = () => dispatch({ type: ActionType.DISMISS_TOAST, toastId: id });

  dispatch({
    type: ActionType.ADD_TOAST,
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) {
          dismiss();
        }
      },
    },
  });

  return {
    id: id,
    dismiss,
    update,
  };
}

function useDefaultToast() {
  const [state, setState] = React.useState<State>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: ActionType.DISMISS_TOAST, toastId }),
  };
}

type UseToastFc = (title: string, props?: Omit<ToasterToast, 'id'>) => void;

const useToast = () => {
  const { toast } = useDefaultToast();
  const useToastError: UseToastFc = (title, props) =>
    toast({
      icon: faTimes,
      variant: 'error',
      title,
      ...props,
    });

  const useToastInfo: UseToastFc = (title, props) =>
    toast({
      icon: faInfo,
      variant: 'info',
      title: title,
      ...props,
    });

  const useToastWarning: UseToastFc = (title, props) =>
    toast({
      icon: faExclamation,
      variant: 'warn',
      title: title,
      ...props,
    });

  const useToastSuccess: UseToastFc = (title, props) =>
    toast({
      icon: faCheck,
      variant: 'success',
      title: title,
      ...props,
    });

  return {
    toast: {
      error: useToastError,
      info: useToastInfo,
      warn: useToastWarning,
      success: useToastSuccess,
    },
  };
};

export { useDefaultToast, toast, useToast };
